'use client';

import { useCallback, useEffect } from 'react';

import { useRouter } from 'next/navigation';

export const ALLOWED_HASH_SUFFIXES = ['hash-'] as const;
export type ModalHashAppRouterId = `${(typeof ALLOWED_HASH_SUFFIXES)[number]}${string}`;

/**
 * useEscapeKeyAppRouter
 * - ESC 키를 눌렀을 때 URL 해시에 특정 접미사가 포함된 경우, router.back()을 호출합니다.
 *
 * 주의 사항:
 * 1. 해당 훅은 전체 페이지에서 **한 번만 호출**되어야 합니다.
 *    - layout.tsx에서 한 번만 등록하십시오.
 *    - 중복 등록 시, ESC 키 이벤트가 중복 처리되어 router.back()이 여러 번 호출될 수 있습니다.
 * 2. 해시가 'hash-'으로 시작하는 경우에만 동작합니다.
 *    - 이 조건은 불필요한 router.back 호출을 방지하기 위한 것입니다.
 */
export const useEscapeKeyAppRouter = () => {
  const router = useRouter();

  // esc 키를 눌렀을 때 키보드 이벤트 핸들러
  const escRouterBack = useCallback(
    (e: KeyboardEvent) => {
      if (
        e.key === 'Escape' &&
        location.hash.length > 1 &&
        ALLOWED_HASH_SUFFIXES.some((t) => location.hash.replace('#', '').startsWith(t))
      ) {
        router.back();
      }
    },
    [router],
  );

  useEffect(() => {
    window.addEventListener('keydown', escRouterBack);
    return () => {
      window.removeEventListener('keydown', escRouterBack);
    };
  }, [escRouterBack]);
};
